import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FiStar, FiEye, FiEyeOff } from 'react-icons/fi';
import { MdDragIndicator } from 'react-icons/md';

const ItemWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0;
  opacity: ${props => props.$isDragging ? 0.5 : 1};
  transform: ${props => props.$transform};
  transition: ${props => props.$transition};
  width: 100%;
  padding: 0;
  margin: 0;
`;

const DragHandle = styled.div`
  display: ${props => props.$visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  color: #666;
  cursor: grab;
  padding: 0;
  margin: 0 4px 0 0;
  width: 20px;
  flex-shrink: 0;
  
  &:active {
    cursor: grabbing;
  }

  svg {
    font-size: 16px;
  }
`;

const ItemContent = styled.div`
  flex: 1;
  position: relative;
  width: 100%;
  padding: 0;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 2px;
  padding-right: 4px;
  opacity: ${props => props.$visible ? 1 : 0};
  transform: ${props => props.$visible ? 'translateX(0)' : 'translateX(8px)'};
  transition: all 0.15s ease;
  width: ${props => props.$visible ? 'auto' : '0'};
  overflow: hidden;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: ${props => props.$active ? 'rgba(76, 175, 80, 0.15)' : 'transparent'};
  border: none;
  border-radius: 6px;
  color: ${props => props.$active ? '#4CAF50' : '#666'};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${props => props.$active ? 'rgba(76, 175, 80, 0.25)' : 'rgba(255, 255, 255, 0.05)'};
    color: ${props => props.$active ? '#4CAF50' : '#aaa'};
  }

  svg {
    font-size: 13px;
  }
`;

export default function MenuItemDraggable({ 
  id, 
  children, 
  editMode, 
  globalEditMode,
  isFavorite, 
  isHidden,
  onToggleFavorite,
  onToggleHidden,
  disabled = false
}) {
  const [itemHovered, setItemHovered] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ 
    id,
    disabled: !globalEditMode || disabled
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const handleMouseEnter = () => {
    if (disabled) return;
    setItemHovered(true);
  };

  const handleMouseLeave = () => {
    setItemHovered(false);
  };

  // Mostrar controles se:
  // 1. editMode global do context está ativo (sempre visível) OU
  // 2. globalEditMode está ativo (2s de hover no menu) E item está com hover
  const controlsVisible = editMode || (globalEditMode && itemHovered);

  return (
    <ItemWrapper
      ref={setNodeRef}
      $transform={style.transform}
      $transition={style.transition}
      $isDragging={isDragging}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <DragHandle 
        $visible={controlsVisible && !disabled}
        {...attributes}
        {...listeners}
      >
        <MdDragIndicator />
      </DragHandle>

      <ItemContent>
        {children}
      </ItemContent>

      <ActionButtons $visible={controlsVisible && !disabled}>
        <ActionButton
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite?.();
          }}
          $active={isFavorite}
          title={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
        >
          <FiStar fill={isFavorite ? 'currentColor' : 'none'} />
        </ActionButton>

        <ActionButton
          onClick={(e) => {
            e.stopPropagation();
            onToggleHidden?.();
          }}
          $active={isHidden}
          title={isHidden ? 'Mostrar item' : 'Ocultar item'}
        >
          {isHidden ? <FiEye /> : <FiEyeOff />}
        </ActionButton>
      </ActionButtons>
    </ItemWrapper>
  );
}
